import type { SessionOutcome } from 'contexttrees';
import { openModal } from './modal';

/**
 * Session-end reconciliation popup: the "manual" half of ContextTrees review.
 *
 * `Director.endSession` has already committed anything whose review mode is `auto`. What's left is
 * whatever the user asked to see first — a design-map recommendation, per-agent memory candidates,
 * or both. Nothing else is committed unless the user presses Apply: dismissing the modal (backdrop,
 * Escape, ✕, Cancel) deliberately drops the recommendation on the floor rather than half-applying it.
 *
 * The promise resolves either way, so the caller's session-end path never hangs on a closed popup.
 */
export async function reviewSessionOutcome(outcome: SessionOutcome): Promise<void> {
  const reviewDesignMap = outcome.designMap.mode === 'manual';
  const reviewAgentMemory = outcome.agentMemory.mode === 'manual';

  // Both branches auto-committed inside endSession — there is nothing to show.
  if (!reviewDesignMap && !reviewAgentMemory) return;

  const designNodes = reviewDesignMap ? collectDesignNodes(outcome) : [];
  const agentEntries = reviewAgentMemory ? Object.entries(outcome.agentMemory.recommendation) : [];
  const agentItemCount = agentEntries.reduce((sum, [, items]) => sum + items.length, 0);

  // Manual review with an empty recommendation is a no-op; don't interrupt with an empty popup.
  if (designNodes.length === 0 && agentItemCount === 0) return;

  return new Promise<void>((resolve) => {
    const title = `Review ContextTrees updates — ${outcome.session.description}`;
    const modal = openModal(title, { onClose: () => resolve() });

    /** nodeId -> checkbox. One checkbox per node covers its content update *and* its activation
     *  request: `designMap.apply(ids)` filters both lists by the same id array. */
    const designChecks = new Map<string, HTMLInputElement>();
    /** agentId -> (concept -> checkbox). */
    const memoryChecks = new Map<string, Map<string, HTMLInputElement>>();

    const intro = document.createElement('p');
    intro.className = 'ct-review-intro';
    intro.textContent =
      'Ticked items get written back to the project when you apply. Closing this without applying commits nothing further.';
    modal.bodyEl.appendChild(intro);

    // ---------- Design map ----------

    if (reviewDesignMap) {
      const section = makeSection('Design map');
      const list = document.createElement('div');
      list.className = 'ct-review-list';

      for (const node of designNodes) {
        const meta: string[] = [];
        if (node.content) meta.push(node.content.status);
        if (node.activation) {
          meta.push(node.activation.active ? 'activate' : 'make dormant');
          if (node.activation.reason) meta.push(node.activation.reason);
        }
        const item = makeCheckItem({
          checked: true,
          title: node.content?.summary || node.id,
          // Activation-only rows have no summary and already read as the id — don't repeat it.
          id: node.content?.summary ? node.id : undefined,
          meta: meta.join(' · '),
          label: `Apply updates to ${node.id}`,
        });
        designChecks.set(node.id, item.input);
        list.appendChild(item.row);
      }

      section.appendChild(list);
      modal.bodyEl.appendChild(section);
    } else {
      modal.bodyEl.appendChild(makeAutoNote('Design map: committed automatically.'));
    }

    // ---------- Agent memory ----------

    if (reviewAgentMemory) {
      const section = makeSection('Agent memory');

      for (const [agentId, items] of agentEntries) {
        const byConcept = new Map<string, HTMLInputElement>();
        memoryChecks.set(agentId, byConcept);

        const agentEl = document.createElement('div');
        agentEl.className = 'ct-review-agent';
        const agentLabel = document.createElement('div');
        agentLabel.className = 'ct-review-agent-label mono';
        agentLabel.textContent = agentId;
        agentEl.appendChild(agentLabel);

        const list = document.createElement('div');
        list.className = 'ct-review-list';
        for (const item of items) {
          const meta = [`${item.action} · score ${item.score.toFixed(2)}`, item.reason].filter(Boolean).join(' · ');
          const row = makeCheckItem({
            // The director's own retain/drop verdict is the default; the user overrides per item.
            checked: item.action === 'retain',
            title: item.group.concept,
            meta,
            label: `Retain ${item.group.concept} for ${agentId}`,
          });
          byConcept.set(item.group.concept, row.input);
          list.appendChild(row.row);
        }
        if (items.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'ct-review-note';
          empty.textContent = 'No memory candidates from this session.';
          list.appendChild(empty);
        }
        agentEl.appendChild(list);

        const parallel = outcome.agentMemory.parallelAgentRecommendations[agentId] ?? [];
        if (parallel.length > 0) {
          const note = document.createElement('div');
          note.className = 'ct-review-note';
          note.textContent = `Suggested parallel agents: ${parallel.map((p) => p.concept).join(', ')}`;
          agentEl.appendChild(note);
        }

        section.appendChild(agentEl);
      }

      modal.bodyEl.appendChild(section);
    } else {
      modal.bodyEl.appendChild(makeAutoNote('Agent memory: committed automatically.'));
    }

    // ---------- Actions ----------

    const actions = document.createElement('div');
    actions.className = 'ct-review-actions';

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn subtle';
    cancelBtn.textContent = 'Discard';
    cancelBtn.title = 'Close without committing any of these updates';
    cancelBtn.addEventListener('click', () => modal.close());
    actions.appendChild(cancelBtn);

    const applyBtn = document.createElement('button');
    applyBtn.className = 'btn primary';
    applyBtn.textContent = 'Apply selected';
    applyBtn.addEventListener('click', () => {
      if (reviewDesignMap) {
        const selectedNodeIds = [...designChecks].filter(([, input]) => input.checked).map(([nodeId]) => nodeId);
        outcome.designMap.apply(selectedNodeIds);
      }
      if (reviewAgentMemory) {
        const selected: Record<string, string[]> = {};
        // Every agentId gets a key even when nothing is ticked: an absent key makes `apply` fall
        // back to the director's own retain/drop verdict, which would ignore the user's deselection.
        for (const [agentId, byConcept] of memoryChecks) {
          selected[agentId] = [...byConcept].filter(([, input]) => input.checked).map(([concept]) => concept);
        }
        outcome.agentMemory.apply(selected);
      }
      modal.close();
    });
    actions.appendChild(applyBtn);

    modal.bodyEl.appendChild(actions);
  });
}

interface DesignNodeRow {
  id: string;
  content?: SessionOutcome['designMap']['recommendation'][number]['content'];
  activation?: SessionOutcome['designMap']['activationRequests'][number];
}

/**
 * Content updates and activation requests are two lists over the same node ids, and `apply` filters
 * both with one array — so they're merged into one row (and one checkbox) per node id here.
 */
function collectDesignNodes(outcome: SessionOutcome): DesignNodeRow[] {
  const byId = new Map<string, DesignNodeRow>();
  for (const rec of outcome.designMap.recommendation) {
    byId.set(rec.nodeId, { ...(byId.get(rec.nodeId) ?? { id: rec.nodeId }), content: rec.content });
  }
  for (const req of outcome.designMap.activationRequests) {
    byId.set(req.nodeId, { ...(byId.get(req.nodeId) ?? { id: req.nodeId }), activation: req });
  }
  return [...byId.values()];
}

function makeSection(label: string): HTMLElement {
  const section = document.createElement('section');
  section.className = 'ct-review-section';
  const labelEl = document.createElement('div');
  labelEl.className = 'ct-review-section-label';
  labelEl.textContent = label;
  section.appendChild(labelEl);
  return section;
}

function makeAutoNote(text: string): HTMLElement {
  const note = document.createElement('div');
  note.className = 'ct-review-note';
  note.textContent = text;
  return note;
}

function makeCheckItem(spec: {
  checked: boolean;
  title: string;
  id?: string;
  meta?: string;
  label: string;
}): { row: HTMLElement; input: HTMLInputElement } {
  const row = document.createElement('label');
  row.className = 'ct-review-item';

  const input = document.createElement('input');
  input.type = 'checkbox';
  input.checked = spec.checked;
  input.setAttribute('aria-label', spec.label);
  row.appendChild(input);

  const body = document.createElement('div');
  body.className = 'ct-review-item-body';

  const title = document.createElement('div');
  title.className = 'ct-review-item-title';
  title.textContent = spec.title;
  body.appendChild(title);

  if (spec.id) {
    const idEl = document.createElement('div');
    idEl.className = 'ct-review-item-id mono';
    idEl.textContent = spec.id;
    body.appendChild(idEl);
  }

  if (spec.meta) {
    const meta = document.createElement('div');
    meta.className = 'ct-review-item-meta';
    meta.textContent = spec.meta;
    body.appendChild(meta);
  }

  row.appendChild(body);
  return { row, input };
}
